/**
 * Donut Chart Component
 * A donut/ring chart for showing proportional data
 */

import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import { formatCompactCurrency } from "@/utils/formatters";

export interface DonutChartDataItem {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutChartDataItem[];
  height?: number;
  showLegend?: boolean;
  title?: string;
  centerLabel?: string;
  centerValue?: string | number;
  formatValue?: (value: number) => string;
}

const defaultFormatValue = (value: number): string =>
  formatCompactCurrency(value);

interface DonutPayloadData {
  name: string;
  value: number;
  color: string;
}

interface TooltipPayloadItem {
  payload: DonutPayloadData;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  formatValue: (value: number) => string;
  total: number;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  formatValue,
  total,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : "0";
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: data.color,
            }}
          />
          <Typography variant="subtitle2" sx={{
            fontWeight: 600
          }}>
            {data.name}
          </Typography>
        </Box>
        <Typography variant="body2">
          {formatValue(data.value)} ({percentage}%)
        </Typography>
      </Box>
    );
  }
  return null;
};

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  height = 250,
  showLegend = true,
  title,
  centerLabel,
  centerValue,
  formatValue = defaultFormatValue,
}) => {
  const theme = useTheme();

  const DEFAULT_COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);

  const chartData = data.map((item, index) => ({
    ...item,
    color: item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
  }));

  const outerRadius = height / 3;
  const innerRadius = outerRadius * 0.6;

  const summaryText = chartData
    .filter((d) => d.value > 0)
    .map((d) => `${d.name}: ${formatValue(d.value)} (${total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)`)
    .join(", ");

  return (
    <Box role="figure" aria-label={title || "Donut Chart"}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600
        }}>
          {title}
        </Typography>
      )}
      <Box className="sr-only" aria-live="polite">
        {summaryText || "No data available"}
      </Box>
      <Box sx={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              fill={theme.palette.chart.categorical[0]}
              dataKey="value"
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip formatValue={formatValue} total={total} />}
            />
            {showLegend && (
              <Legend
                formatter={(value: string) => (
                  <span style={{ color: theme.palette.text.primary, fontSize: 12 }}>
                    {value}
                  </span>
                )}
              />
            )}
          </PieChart>
        </ResponsiveContainer>

        {/* Center label */}
        {(centerLabel || centerValue) && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            {centerValue && (
              <Typography variant="h5" sx={{
                fontWeight: 700
              }}>
                {centerValue}
              </Typography>
            )}
            {centerLabel && (
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                {centerLabel}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DonutChart;
